// Singleton SignalR connection for Group Chat.
//
// This module (not a React hook/component) owns the ONE hub connection.
// Presence (UserOnline/UserOffline) on the backend is tied 1:1 to this
// connection being alive — there is no separate "away" state (see
// fe-handoff.md §6 table) — so by product decision the connection is only
// kept up while the user is actively on the Chat page AND the browser
// tab is focused/visible (see useChatSocket.js). Leaving the chat page,
// switching tabs, or minimizing the browser tears the connection down so
// the user shows "offline" to others immediately, rather than staying
// "online" for the rest of the session just because they're still logged
// in elsewhere in the app. The trade-off: the sidebar unread badge only
// updates live while actually on the chat page, not app-wide.
//
// Because of that, the connection CAN be recreated (a brand-new
// HubConnection instance) while a MessageThread for some group is still
// conceptually "open" from React's perspective — e.g. tab loses then
// regains focus while ChatPage stays mounted. `startChatConnection` fires
// the same `reconnectedListeners` set on every fresh connect, not only on
// SignalR's own automatic mid-session reconnect, so subscribers (see
// MessageThread) re-attach their per-group handlers to whichever
// connection object is currently live instead of silently listening to a
// dead one. Page-level components read `getChatConnection()` and add
// their own additional `.on(...)` handlers for the events they care about
// (SignalR calls every handler registered for an event name, so this
// composes fine with the baseline handlers registered here).
import * as signalR from '@microsoft/signalr';
import { store } from '../../redux/store';
import { ChatHubUrl } from '../../Api/Api';
import { setChatUnreadCount } from '../../redux/slices/chatSlice';
import { selectUserId } from '../../redux/slices/authSlice';

let connection = null;
let startPromise = null;
let activeGroupId = null;
const unreadByGroup = new Map();
let readyWaiters = [];
// Listeners for "the group list may be stale, re-fetch GetGroups" (doc §5.2
// AddedToGroup/RemovedFromGroup/GroupDeleted/GroupUpdated, §6 point 3) and
// for hub reconnection (doc §6). These are OUR OWN listener sets (Set, with
// proper add/remove), unlike signalR's own onreconnected/onclose/
// onreconnecting callbacks, which have no "off" — registering one of those
// per component mount (e.g. once per MessageThread mount, since ChatPage
// remounts it per selected group) would leak a new callback on every mount
// for the life of the singleton connection. So the hub-level
// conn.onreconnected(...) is registered exactly ONCE below, in
// startChatConnection, and fans out to these listener sets instead.
const groupsChangedListeners = new Set();
const reconnectedListeners = new Set();
// Per-group "who's typing" state, kept here (not in MessageThread) so the
// sidebar list can show "X is typing…" for ANY group, not just the one
// currently open — mirrors unreadByGroup above. Each entry auto-clears after
// 4s of silence, same window MessageThread uses for its own in-thread badge.
const typingByGroup = new Map();
const typingListeners = new Set();
// Global "who's online" presence. UserOnline/UserOffline fire only on a
// user's first-connection-open / last-connection-close edge (fe-handoff.md
// §3), never as a full roster on connect — so this Set is a record of
// DELTAS observed since this connection came up, not a complete known
// state. Consumers still need to seed their initial view from a REST
// snapshot (e.g. GetGroupInfo's members[].isOnline) and use this registry
// only to overlay updates for specific userIds seen after that snapshot.
const onlineUserIds = new Set();
const presenceListeners = new Set();

const recomputeUnread = () => {
    let total = 0;
    unreadByGroup.forEach((v) => { total += v; });
    store.dispatch(setChatUnreadCount(total));
};

/** The group currently open/visible on screen, if any — used to (a) avoid
 * double-counting unread for a group the user is actively looking at, and
 * (b) drive the doc's §6 reconnect catch-up for "the currently open group". */
export const setActiveChatGroupId = (id) => { activeGroupId = id ?? null; };
export const getActiveChatGroupId = () => activeGroupId;

// Whether ChatPage is currently mounted — one of the two inputs (along with
// tab visibility/focus) useChatSocket combines to decide whether the hub
// connection should be up at all. Plain module state + listener set, same
// pattern as the other "signal a top-level hook" pieces in this file, since
// ChatPage and useChatSocket (mounted at DashboardLayout) aren't in the same
// component tree.
let chatPageOpen = false;
const chatPageOpenListeners = new Set();
export const setChatPageOpen = (open) => {
    chatPageOpen = Boolean(open);
    chatPageOpenListeners.forEach((cb) => { try { cb(chatPageOpen); } catch { /* ignore */ } });
};
export const getChatPageOpen = () => chatPageOpen;
export const onChatPageOpenChanged = (cb) => {
    chatPageOpenListeners.add(cb);
    return () => chatPageOpenListeners.delete(cb);
};

/** Called by ConversationList after a GetGroups fetch to seed the manager's
 * per-group unread map (each group DTO is expected to carry its own
 * unreadCount) so the sidebar badge reflects the true total immediately. */
export const seedChatUnreadCounts = (groups) => {
    unreadByGroup.clear();
    (groups || []).forEach((g) => {
        const id = g?.groupId;
        if (id == null) return;
        unreadByGroup.set(id, Math.max(0, Number(g.unreadCount || 0)));
    });
    recomputeUnread();
};

/** Called after a successful MarkRead for a group. */
export const markChatGroupRead = (groupId) => {
    if (groupId == null) return;
    unreadByGroup.set(groupId, 0);
    recomputeUnread();
};

const bumpUnread = (groupId) => {
    if (groupId == null) return;
    unreadByGroup.set(groupId, (unreadByGroup.get(groupId) || 0) + 1);
    recomputeUnread();
};

const notifyGroupsChanged = () => {
    groupsChangedListeners.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
};

const notifyTypingChanged = (groupId) => {
    const name = typingByGroup.get(groupId)?.name || null;
    typingListeners.forEach((cb) => { try { cb(groupId, name); } catch { /* ignore */ } });
};

const notifyPresenceChanged = (userId, online) => {
    presenceListeners.forEach((cb) => { try { cb(userId, online); } catch { /* ignore */ } });
};

/** True only if an online/offline edge for this user has actually been
 * observed since the current connection came up — see onlineUserIds above.
 * Callers should treat a `false` result as "unknown", not "confirmed
 * offline", unless they've also seen an explicit UserOffline for this user
 * via onChatPresence. */
export const isUserOnline = (userId) => onlineUserIds.has(userId);

/** Subscribe to presence deltas: fires (userId, true|false) on every
 * UserOnline/UserOffline. Returns an unsubscribe function. */
export const onChatPresence = (cb) => {
    presenceListeners.add(cb);
    return () => presenceListeners.delete(cb);
};

/** Subscribe to "someone is typing in <groupId>" changes, for the sidebar
 * list — fires with (groupId, name) on start, and (groupId, null) once the
 * 4s silence window clears it. Returns an unsubscribe function. */
export const onChatTyping = (cb) => {
    typingListeners.add(cb);
    return () => typingListeners.delete(cb);
};

/** Message-preview updates for the sidebar list — fired on every
 * ReceiveMessage regardless of which group is open, so ConversationList can
 * update its lastMessageText/lastMessageOn/lastMessageSenderName live
 * instead of only after the next GetGroups refetch. Returns an unsubscribe
 * function. */
const messageListeners = new Set();
export const onChatMessage = (cb) => {
    messageListeners.add(cb);
    return () => messageListeners.delete(cb);
};

/** Subscribe to "the group list may be stale" — fired on AddedToGroup,
 * RemovedFromGroup, GroupDeleted and GroupUpdated (doc §5.2), so any
 * mounted screen can re-call GetGroups rather than each hub event needing
 * its own bespoke list-patching logic. Returns an unsubscribe function. */
export const onChatGroupsChanged = (cb) => {
    groupsChangedListeners.add(cb);
    return () => groupsChangedListeners.delete(cb);
};

/** Subscribe to hub reconnection (doc §6). Registered against the
 * singleton connection's own `onreconnected` exactly once (see
 * startChatConnection) — components add/remove themselves from this set on
 * mount/unmount instead of ever calling `connection.onreconnected(...)`
 * directly, since signalR provides no way to unregister those. */
export const onChatReconnected = (cb) => {
    reconnectedListeners.add(cb);
    return () => reconnectedListeners.delete(cb);
};

const registerBaselineHandlers = (conn) => {
    // Keeps the sidebar badge live for groups the user isn't currently
    // viewing — this is the reason the connection has to outlive the chat
    // page. Page-level components register their OWN ReceiveMessage handler
    // too, for rendering the message into an open thread.
    conn.on('ReceiveMessage', (msg) => {
        if (!msg || msg.groupId == null) return;
        // A message from this sender means they're done typing — clear it
        // immediately rather than waiting out the 4s timeout.
        const typing = typingByGroup.get(msg.groupId);
        if (typing && msg.senderUserId != null && typing.userId === msg.senderUserId) {
            clearTimeout(typing.timer);
            typingByGroup.delete(msg.groupId);
            notifyTypingChanged(msg.groupId);
        }
        messageListeners.forEach((cb) => { try { cb(msg); } catch { /* ignore */ } });
        if (msg.messageType === 'system') return;
        if (msg.groupId === activeGroupId) return;
        const myId = selectUserId(store.getState());
        if (myId != null && msg.senderUserId === myId) return;
        bumpUnread(msg.groupId);
    });
    conn.on('Typing', (p) => {
        if (!p || p.groupId == null) return;
        const myId = selectUserId(store.getState());
        if (myId != null && p.userId === myId) return;
        const existing = typingByGroup.get(p.groupId);
        if (existing?.timer) clearTimeout(existing.timer);
        const timer = setTimeout(() => {
            typingByGroup.delete(p.groupId);
            notifyTypingChanged(p.groupId);
        }, 4000);
        typingByGroup.set(p.groupId, { userId: p.userId, name: p.name, timer });
        notifyTypingChanged(p.groupId);
    });
    conn.on('UserOnline', (p) => {
        if (p?.userId == null) return;
        onlineUserIds.add(p.userId);
        notifyPresenceChanged(p.userId, true);
    });
    conn.on('UserOffline', (p) => {
        if (p?.userId == null) return;
        onlineUserIds.delete(p.userId);
        notifyPresenceChanged(p.userId, false);
    });
    conn.on('AddedToGroup', () => {
        // Sent only to the affected user's own connections (doc §5.2) —
        // simplest correct handling is "your list may be stale, refetch."
        notifyGroupsChanged();
    });
    conn.on('RemovedFromGroup', (p) => {
        if (p?.groupId == null) return;
        unreadByGroup.delete(p.groupId);
        recomputeUnread();
        notifyGroupsChanged();
    });
    conn.on('GroupDeleted', (p) => {
        if (p?.groupId == null) return;
        unreadByGroup.delete(p.groupId);
        recomputeUnread();
        notifyGroupsChanged();
    });
    conn.on('GroupUpdated', () => {
        // Generic "something changed, your cached info may be stale" signal
        // (doc §5.2) — a safety-net GetGroups refresh keeps names/previews
        // in the conversation list from going stale too.
        notifyGroupsChanged();
    });
};

export const getChatConnection = () => connection;

/** Resolves with the live connection once it's up. If already connected,
 * resolves on the next microtask (never synchronously — keeps callers'
 * effect-cleanup logic simple and consistent). */
export const onChatConnectionReady = (cb) => {
    let cancelled = false;
    if (connection) {
        Promise.resolve().then(() => { if (!cancelled) cb(connection); });
    } else {
        readyWaiters.push(cb);
    }
    return () => {
        cancelled = true;
        readyWaiters = readyWaiters.filter((f) => f !== cb);
    };
};

export const startChatConnection = () => {
    if (connection || startPromise) return startPromise;

    const conn = new signalR.HubConnectionBuilder()
        .withUrl(ChatHubUrl, {
            // A function, not the token itself — called fresh on every
            // (re)connect attempt so a refreshed token is picked up
            // automatically instead of retrying with a stale one.
            accessTokenFactory: () => store.getState()?.auth?.token || '',
            // Auth here is entirely the bearer token above, not cookies. The
            // hub's CORS policy returns Access-Control-Allow-Origin: * (no
            // per-origin echo), which browsers reject for a credentialed
            // (cookie-carrying) request — SignalR defaults withCredentials
            // to true, so without this flag the browser blocks the
            // negotiate preflight before it ever reaches the server.
            withCredentials: false,
        })
        .withAutomaticReconnect()
        .build();

    // Register every handler BEFORE start() so nothing is missed in the
    // split second right after connecting (doc §3).
    registerBaselineHandlers(conn);

    // Registered exactly ONCE per connection (signalR has no way to
    // unregister an onreconnected callback), and fans out to
    // reconnectedListeners — see onChatReconnected above. Doc §6: on
    // reconnect, catch up the currently-open group via FetchMessages?after=
    // (MessageThread subscribes for this) and refresh GetGroups for
    // everything else (ChatPage subscribes for this).
    conn.onreconnected(() => {
        reconnectedListeners.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
    });

    startPromise = conn.start()
        .then(() => {
            connection = conn;
            startPromise = null;
            const waiters = readyWaiters;
            readyWaiters = [];
            waiters.forEach((cb) => cb(conn));
            // Treat "freshly (re)connected" the same whether it's SignalR's
            // own automatic reconnect (conn.onreconnected below) or us
            // having torn down and rebuilt the connection object entirely
            // (see module comment) — either way, subscribers need to catch
            // up on missed events and re-attach to the current connection.
            reconnectedListeners.forEach((cb) => { try { cb(); } catch { /* ignore */ } });
        })
        .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Chat hub connection failed to start.', err);
            startPromise = null;
            connection = null;
        });
    return startPromise;
};

export const stopChatConnection = () => {
    const conn = connection;
    connection = null;
    startPromise = null;
    activeGroupId = null;
    unreadByGroup.clear();
    typingByGroup.forEach((t) => clearTimeout(t.timer));
    typingByGroup.clear();
    onlineUserIds.clear();
    readyWaiters = [];
    if (conn) conn.stop().catch(() => {});
};
